import { observable } from 'mobx';
import { autobind } from 'core-decorators';
import BaseStore from '@src/utils/store/base-store';
import useMount from '@src/utils/hooks/use-mount';
import Toast from '@src/components/toast';
import BusinessAccountInfoStore from '@src/business-components/business-account-info';
import rootStore from '@src/mobx-store/root-store';
import { getActivityRulesAsync } from '@src/services/activity-rules';

const LOAD_FAILED_MESSAGE = '内容加载失败，请稍后重试';
const NAVIGATION_ROUTE_KEYS = ['TEAM', 'BACKPACK'];

class RulesStore extends BaseStore {
  loadRules;

  activitySessionStore;

  accountInfoStore = new BusinessAccountInfoStore();

  @observable loadStatus = 'idle';

  // 稳定的 CMS ViewModel；null 保证加载与错误态绝不暴露过期或示例内容
  @observable.ref rule = null;

  requestPromise = null;

  requestVersion = 0;

  disposed = false;

  /**
   * 构建带可替换 OMNI 适配器的页面 owner，供聚焦的 Store 测试注入。
   * @param {Object} options 页面依赖。
   * @param {object} options.activitySessionStore 共享活动会话 owner（底栏组队域门控的 reviewCleared 数据源）。
   * @param {Function} options.loadRules 稳定的活动规则 provider。
   */
  constructor({ activitySessionStore = rootStore.activitySessionStore, loadRules = getActivityRulesAsync } = {}) {
    super();
    this.activitySessionStore = activitySessionStore;
    this.loadRules = loadRules;
  }

  /** 注册路由状态，为本页挂载实例加载一次 CMS 内容。 */
  use() {
    super.use();
    useMount(() => {
      this.disposed = false;
      this.loadActivityRules();
      return this.dispose;
    });
  }

  /**
   * 路由卸载时使在途回调失效，旧响应无法重绘另一次访问。
   * @returns {void}
   */
  @autobind
  dispose() {
    this.disposed = true;
    this.requestVersion += 1;
    this.requestPromise = null;
  }

  /**
   * 加载或重试 CMS 规则内容，重复点击共享同一条在途请求。
   * 请求与响应失败收敛到同一个可重试的页面状态并公开 Toast。
   * @returns {Promise<Object|undefined>} 已加载的规则 ViewModel；失败已处理或过期完成时返回 undefined。
   */
  @autobind
  loadActivityRules() {
    // 底栏组队域门控数据源（2026-09-20）：规则页直达场景共享会话可能尚未初始化，
    // 静默补拉一次 get_config 供 reviewCleared 判断（有缓存时复用不重发）；
    // 失败静默保持 reviewStatus null，底栏按"未完成回顾"保守回落首页分流
    this.activitySessionStore.ensureInitialized().catch(() => undefined);

    if (this.requestPromise) {
      return this.requestPromise;
    }

    const requestVersion = ++this.requestVersion;
    this.loadStatus = 'loading';
    this.rule = null;

    const request = Promise.resolve()
      .then(() => this.loadRules())
      .then(
        rule => {
          if (this.disposed || requestVersion !== this.requestVersion) {
            return undefined;
          }
          this.rule = rule;
          this.loadStatus = 'ready';
          return rule;
        },
        () => {
          if (this.disposed || requestVersion !== this.requestVersion) {
            return undefined;
          }
          this.rule = null;
          this.loadStatus = 'error';
          Toast.fail(LOAD_FAILED_MESSAGE);
          return undefined;
        }
      );

    const ownedRequest = request.then(result => {
      if (this.requestPromise === ownedRequest) {
        this.requestPromise = null;
      }
      return result;
    });
    this.requestPromise = ownedRequest;
    return ownedRequest;
  }

  /**
   * 处理 MoBottomTabs 发出的路由 key；当前 RULES Tab 刻意停留本路由。
   * @param {string} routeKey 受控底栏路由 key。
   * @returns {void}
   */
  @autobind
  navigateByTab(routeKey) {
    if (NAVIGATION_ROUTE_KEYS.indexOf(routeKey) === -1) {
      return;
    }

    if (routeKey === 'TEAM' && !this.activitySessionStore.reviewCleared) {
      this.redirect('MAIN');
      return;
    }
    this.redirect(routeKey);
  }
}

export default RulesStore;
